import React from "react";
import { PlxsText, PlxsCheckbox } from "@plexlogic/plexus-ui";
import { EntriesUpload } from "../../types/DrawData";
import { getHeaderRowColumns, getEntryRow1Columns, getEntryRow2Columns } from "./useUploadStore";
import { toSentenceCase } from "../../utils/stringUtils";
import { isNumeric } from "../../utils/validationUtils";

type QuestionMode =
  | "table-only"
  | "ticket-count-selection"
  | "unique-id-selection"
  | "multi-column-selection";

interface ConfirmationTableProps {
  entriesUpload: EntriesUpload;
  selectedColumns?: string[];
  onColumnToggle?: (column: string) => void;
  allowSelection?: boolean;
  numericOnlyColumns?: string[];
  ticketCountColumn?: string;
  uniqueIdColumn?: string;
  uniqueIdentifierColumns?: string[];
  // Questionnaire mode props
  mode?: QuestionMode;
  onTicketCountColumnChange?: (column: string | null) => void;
  onUniqueIdColumnChange?: (column: string | null) => void;
  onUniqueIdentifierColumnsChange?: (columns: string[]) => void;
  // Display mode for entry row case
  isEntryRow?: boolean;
  isReadOnly?: boolean;
}

const ConfirmationTable: React.FC<ConfirmationTableProps> = ({
  entriesUpload,
  selectedColumns = [],
  onColumnToggle,
  allowSelection = false,
  numericOnlyColumns = [],
  ticketCountColumn,
  uniqueIdColumn,
  uniqueIdentifierColumns = [],
  mode = "table-only",
  onTicketCountColumnChange,
  onUniqueIdColumnChange,
  onUniqueIdentifierColumnsChange,
  isEntryRow = false,
  isReadOnly = false,
}) => {
  if (!getHeaderRowColumns(entriesUpload).length && !getEntryRow1Columns(entriesUpload).length) {
    return null;
  }

  // Get columns - handle entry row case differently
  const columns = isEntryRow
    ? getEntryRow1Columns(entriesUpload).map((_, idx) => `Column ${idx + 1}`)
    : getHeaderRowColumns(entriesUpload).map(header => toSentenceCase(header.trim())).filter(Boolean) ||
      getEntryRow1Columns(entriesUpload).map((_, idx) => `Column ${idx + 1}`) ||
      [];

  // Automatically validate and identify numeric columns
  const validateNumericColumns = (): string[] => {
    const numericCols: string[] = [];

    // For entry row case, validate headerRowColumns (first data row) and entryRow1Columns (second data row)
    if (isEntryRow) {
      const headerColumns = getHeaderRowColumns(entriesUpload);
      const entryColumns = getEntryRow1Columns(entriesUpload);
      if (headerColumns.length && entryColumns.length) {
        columns.forEach((column, idx) => {
          const val1 = headerColumns[idx]; // First data row
          const val2 = entryColumns[idx]; // Second data row

          if (isNumeric(val1) && isNumeric(val2)) {
            numericCols.push(column);
          }
        });
      }
    } else {
      // For header row case, use the original logic
      const entryRow1 = getEntryRow1Columns(entriesUpload);
      const entryRow2 = getEntryRow2Columns(entriesUpload);
      if (entryRow1.length && entryRow2.length) {
        columns.forEach((column, idx) => {
          const val1 = entryRow1[idx];
          const val2 = entryRow2[idx];

          if (isNumeric(val1) && isNumeric(val2)) {
            numericCols.push(column);
          }
        });
      }
    }

    return numericCols;
  };

  const validatedNumericColumns =
    numericOnlyColumns.length > 0
      ? numericOnlyColumns
      : validateNumericColumns();

  // Determine which columns should be disabled based on mode
  const getDisabledColumns = (): string[] => {
    switch (mode) {
      case "ticket-count-selection":
        // Only allow numeric columns for ticket count selection
        // Use case-insensitive comparison to handle column name variations
        return columns.filter((col) => 
          !validatedNumericColumns.some(numCol => 
            numCol.toLowerCase() === col.toLowerCase()
          )
        );
      case "unique-id-selection":
        // All columns are available for unique ID selection
        return [];
      case "table-only":
      default:
        return [];
    }
  };

  const disabledColumns = getDisabledColumns();

  // Handle column selection based on mode
  const handleColumnSelection = (column: string) => {
    if (isReadOnly || disabledColumns.includes(column)) return;

    switch (mode) {
      case "ticket-count-selection":
        // Single selection for ticket count
        const newTicketCount = ticketCountColumn === column ? null : column;
        onTicketCountColumnChange?.(newTicketCount);
        break;
      case "unique-id-selection":
        // Single selection for unique ID
        const newUniqueId = uniqueIdColumn === column ? null : column;
        onUniqueIdColumnChange?.(newUniqueId);
        break;
      case "multi-column-selection":
        // Multi-selection for unique identifier columns
        const currentColumns = uniqueIdentifierColumns || [];
        const newColumns = currentColumns.includes(column)
          ? currentColumns.filter((col) => col !== column)
          : [...currentColumns, column];
        onUniqueIdentifierColumnsChange?.(newColumns);
        break;
      case "table-only":
      default:
        // Standard multi-selection for table viewing
        onColumnToggle?.(column);
        break;
    }
  };

  // Determine if a column is selected based on mode
  const isColumnSelected = (column: string): boolean => {
    switch (mode) {
      case "ticket-count-selection":
        return ticketCountColumn === column;
      case "unique-id-selection":
        return uniqueIdColumn === column;
      case "multi-column-selection":
        return (uniqueIdentifierColumns || []).includes(column);
      case "table-only":
      default:
        return selectedColumns.includes(column);
    }
  };

  // Get data rows for preview - include headerRowColumns for entry row case
  const dataRows = isEntryRow
    ? ([
        getHeaderRowColumns(entriesUpload),
        getEntryRow1Columns(entriesUpload),
        getEntryRow2Columns(entriesUpload),
      ].filter(row => row.length > 0))
    : ([getEntryRow1Columns(entriesUpload), getEntryRow2Columns(entriesUpload)].filter(
        row => row.length > 0
      ));

  return (
    <div
      style={{
        marginTop: "var(--plexus-space-lg, 24px)",
        border: "1px solid var(--plexus-border-subtle, #e5e7eb)",
        borderRadius: "var(--plexus-radius-md, 8px)",
        overflow: "hidden",
      }}
    >
      {/* Table */}
      <div style={{ overflowX: "auto" }}>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "14px",
          }}
        >
          {/* Header row with checkboxes */}
          <thead>
            <tr
              style={{
                backgroundColor: "var(--plexus-surface-subtle, #f9fafb)",
              }}
            >
              {columns.map((column, idx) => {
                const isDisabled = disabledColumns.includes(column);
                const isSelected = isColumnSelected(column);

                return (
                  <th
                    key={idx}
                    style={{
                      padding: "var(--plexus-space-sm, 8px)",
                      textAlign: "left",
                      borderBottom:
                        "1px solid var(--plexus-border-subtle, #e5e7eb)",
                      borderRight:
                        idx < columns.length - 1
                          ? "1px solid var(--plexus-border-subtle, #e5e7eb)"
                          : "none",
                      minWidth: "120px",
                      opacity: isDisabled ? 0.5 : 1,
                      backgroundColor: isDisabled
                        ? "var(--plexus-surface-disabled, #f3f4f6)"
                        : "var(--plexus-surface-subtle, #f9fafb)",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "var(--plexus-space-xs, 4px)",
                      }}
                    >
                      {(allowSelection || mode !== "table-only") && (
                        <PlxsCheckbox
                          dataTestId={`column-checkbox-${idx}`}
                          value={isSelected}
                          onChange={() => handleColumnSelection(column)}
                          isDisabled={isReadOnly || isDisabled}
                        />
                      )}
                      <PlxsText size="sm" weight="bold">
                        {column}
                        {ticketCountColumn === column && (
                          <span
                            style={{
                              marginLeft: "var(--plexus-space-xs, 4px)",
                              color: "var(--plexus-color-primary, #0066cc)",
                              fontWeight: "bold",
                            }}
                          >
                            (Ticket count)
                          </span>
                        )}
                      </PlxsText>
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>

          {/* Data rows */}
          <tbody>
            {dataRows.map((row, rowIdx) => (
              <tr key={rowIdx}>
                {row.map((cell, cellIdx) => {
                  const column = columns[cellIdx];
                  const isDisabled = disabledColumns.includes(column);
                  const isSelected = isColumnSelected(column);

                  return (
                    <td
                      key={cellIdx}
                      style={{
                        padding: "var(--plexus-space-sm, 8px)",
                        borderBottom:
                          rowIdx < dataRows.length - 1
                            ? "1px solid var(--plexus-border-subtle, #e5e7eb)"
                            : "none",
                        borderRight:
                          cellIdx < row.length - 1
                            ? "1px solid var(--plexus-border-subtle, #e5e7eb)"
                            : "none",
                        backgroundColor: isDisabled
                          ? "var(--plexus-surface-disabled, #f3f4f6)"
                          : (allowSelection || mode !== "table-only") &&
                            isSelected
                          ? "var(--plexus-surface-brand-light, #f0f7ff)"
                          : "transparent",
                        opacity: isDisabled
                          ? 0.5
                          : (!allowSelection && mode === "table-only") ||
                            isSelected
                          ? 1
                          : 0.6,
                      }}
                    >
                      <PlxsText size="sm">{cell ? toSentenceCase(cell) : "-"}</PlxsText>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ConfirmationTable;
